import { Component, OnInit, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { faCalendar, faCheckCircle, faInfoCircle, faPhone, faRefresh } from '@fortawesome/pro-light-svg-icons';
import { ACTIVITIES_NAMESPACE, ATTENDANCES_NAMESPACE, I18nService } from '@pflegix/i18n';
import { BusinessPartnerType, OrderPopulatedResponseDto } from '@pflegix/types';
import {
  AppIconService,
  EntityHeaderBaseDataComponent,
  IconWithTextComponent,
  PfxTemplateDirective,
} from '@pflegix/ui';
import { isDefined } from '@pflegix/util';
import { ITranslationOptions } from 'angular-i18next';
import { TooltipModule } from 'primeng/tooltip';
import { OrderStore } from '../../../store';
import { getFrequencyFromVolumes, hasCareService } from '../../../util';
import {
  ORDER_HEADER_DATA_I18N_NAMESPACE,
  ORDER_HEADER_DATA_TRANSLATION_DE,
  ORDER_HEADER_DATA_TRANSLATION_EN,
} from './i18n';

@Component({
  selector: 'pfx-order-header-data',
  imports: [EntityHeaderBaseDataComponent, IconWithTextComponent, PfxTemplateDirective, RouterLink, TooltipModule],
  templateUrl: './order-header-data.component.html',
})
export class OrderHeaderDataComponent implements OnInit {
  private readonly i18n = inject(I18nService);
  private readonly appIconService = inject(AppIconService);

  public orderId = input.required<string>();

  protected icons = {
    phone: faPhone,
    settlementType: faInfoCircle,
    period: faCalendar,
    attendance: faCheckCircle,
    frequency: faRefresh,
    documentNumber: this.appIconService.getDocumenNumberIcon()?.icon,
  };

  protected dependentIcon = this.appIconService.getBusinessPartnerIconByType(BusinessPartnerType.DEPENDENT);

  protected caregiverIcon = this.appIconService.getBusinessPartnerIconByType(BusinessPartnerType.CAREGIVER_INTERNAL);

  protected dependentName = computed<string | undefined>(() => {
    const order = this.order();

    if (!isDefined(order)) {
      return undefined;
    }

    return `${order.partners.dependent.mainAddress.lastname}, ${order.partners.dependent.mainAddress.firstname}`;
  });

  protected documentNumber = computed<string | undefined>(() => {
    const order = this.order();

    if (!isDefined(order)) {
      return undefined;
    }

    return order.documentNumber;
  });

  protected dependentPhone = computed<string | undefined>(() => {
    const order = this.order();

    if (!isDefined(order)) {
      return undefined;
    }

    return order.partners.dependent.mainAddress.mobile?.value ?? order.partners.dependent.mainAddress.phone?.value;
  });

  protected caregiverName = computed<string | undefined>(() => {
    const order = this.order();

    if (!isDefined(order) || !hasCareService(order)) {
      return undefined;
    }

    const { caregiver } = order.services.care.partners;

    return caregiver ? `${caregiver.mainAddress?.lastname}, ${caregiver.mainAddress?.firstname}` : undefined;
  });

  protected caregiverPhone = computed<string | undefined>(() => {
    const order = this.order();

    if (!isDefined(order) || !hasCareService(order)) {
      return undefined;
    }

    const { caregiver } = order.services.care.partners;

    return caregiver ? caregiver.mainAddress?.mobile?.value : undefined;
  });

  protected attendances = computed<string[] | undefined>(() =>
    this.order()?.services.care?.attendances?.map((attendance) =>
      this.i18n.t(attendance.toLowerCase(), { ns: ATTENDANCES_NAMESPACE })
    )
  );

  protected activities = computed<string[] | undefined>(() =>
    this.order()?.services.care?.activities?.map((activity) =>
      this.i18n.t(activity.toLowerCase(), { ns: ACTIVITIES_NAMESPACE })
    )
  );

  protected period = computed<string | undefined>(() => {
    const order = this.order();

    if (!isDefined(order) || !hasCareService(order)) {
      return undefined;
    }

    const {
      period: { startAt, endAt },
    } = order.services.care;

    return this.getComponentTranslation(endAt ? 'period' : 'period_open_end', {
      startAt: new Date(startAt),
      endAt: endAt ? new Date(endAt) : undefined,
    });
  });

  protected frequency = computed<string | undefined>(() => {
    const volume = this.order()?.services.care?.volume;

    if (!volume) {
      return undefined;
    }

    const { rhythm, visits, duration } = getFrequencyFromVolumes(volume);

    return this.getComponentTranslation('frequency', {
      rhythm,
      visits,
      duration,
    });
  });

  protected order = computed<OrderPopulatedResponseDto | null>(() => this.orderStore.entityMap()[this.orderId()]);

  private orderStore = inject(OrderStore);

  public ngOnInit(): void {
    this.lazyLoadTranslations();
  }

  private lazyLoadTranslations(): void {
    this.i18n.addTranslations({
      namespace: ORDER_HEADER_DATA_I18N_NAMESPACE,
      de: ORDER_HEADER_DATA_TRANSLATION_DE,
      en: ORDER_HEADER_DATA_TRANSLATION_EN,
    });
  }

  private getComponentTranslation(key: string, options: ITranslationOptions = {}): string {
    return this.i18n.t(key, { ...options, ns: ORDER_HEADER_DATA_I18N_NAMESPACE });
  }
}
